import React from 'react';
import { Drawer, Empty, Table, Tag, Tooltip, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getReportDispatches, type FinancialReportDispatch } from '@/services/reportDocuments.service';

const { Text } = Typography;

interface Props {
  open: boolean;
  reportKey?: string;
  partyType?: string;
  partyId?: string;
  /** Limits history to one document (quotation, purchase order, estimation). */
  documentId?: string;
  onClose: () => void;
}

export const reportDispatchesQueryKey = (filter: { key?: string; party_type?: string; party_id?: string; document_id?: string }) => ['report-dispatches', filter];

const ReportSendHistory: React.FC<Props> = ({ open, reportKey, partyType, partyId, documentId, onClose }) => {
  const filter = documentId ? { key: reportKey, document_id: documentId } : { key: reportKey, party_type: partyType, party_id: partyId };
  const { data = [], isFetching } = useQuery({
    queryKey: reportDispatchesQueryKey(filter),
    queryFn: () => getReportDispatches(filter),
    enabled: open,
  });

  return (
    <Drawer open={open} onClose={onClose} title="Send history" width={760}>
      <Table<FinancialReportDispatch>
        size="small"
        rowKey="id"
        loading={isFetching}
        dataSource={data}
        pagination={{ pageSize: 15 }}
        locale={{ emptyText: <Empty description="Not emailed yet" /> }}
        columns={[
          { title: 'Sent', dataIndex: 'sent_at', width: 140, render: (v: string) => dayjs(v).format('DD-MM-YYYY HH:mm') },
          {
            title: 'Document',
            key: 'doc',
            render: (_, r) => (
              <div>
                <Text strong>{r.document_ref}</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {r.title}
                    {r.party_name ? ` - ${r.party_name}` : ''}
                  </Text>
                </div>
              </div>
            ),
          },
          {
            title: 'Recipients',
            key: 'to',
            render: (_, r) => (
              <div>
                {r.recipients.join(', ')}
                {r.cc.length > 0 && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      CC: {r.cc.join(', ')}
                    </Text>
                  </div>
                )}
              </div>
            ),
          },
          { title: 'By', dataIndex: 'sent_by_name', width: 120, render: (v: string | null) => v || '-' },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 90,
            render: (v: string, r) =>
              v === 'Sent' ? (
                <Tag color="success">Sent</Tag>
              ) : (
                <Tooltip title={r.error_message}>
                  <Tag color="error">Failed</Tag>
                </Tooltip>
              ),
          },
        ]}
      />
    </Drawer>
  );
};

export default ReportSendHistory;
